/**
 * GoogleAnalyticsService.js
 * Created on 10-mrt-2016
 * 
 * WISH: methods for session start/end? https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#session
 * For now, you can workaround this by calling GoogleAnalyticsService.trackPageview('pageview', {'sessionControl': 'end'});
 * 
 * @author Reinier Feijen for Ranj
 **/
export default class GoogleAnalyticsService {

	/**
	 * Initialize the service once at startup.
	 * @param {String} trackerID As obtained for GA property.
	 * @param {Function} callback Callback which will be called when initialization is complete. May be called right away/sync!
	 * @param {Object} parameters Optional parameters to set on the tracker. See https://developers.google.com/analytics/devguides/collection/analyticsjs/creating-trackers#the_create_method
	 * @param {Boolean} dontAnonymizeIp Optional parameter to turn off ip anonymization. Default: false. In 99.9% of cases the default 'false' will be required.
	 * @returns {undefined}
	 */
	initialize(trackerID, callback, parameters, dontAnonymizeIp) {
		this.setEnabled(true);
		this.setQueueEnabled(true);
		this._callQueue = [];
		//start interval for replenishing hit quotum
		this._hitCount = 0;
		this._hitsRemaining = HITS_INITIAL;
		this._replenishInterval = setInterval(this._replenish.bind(this), HITS_REPLENISH_INTERVAL);
		//
		this.ga = window.ga;
		//
		if (!parameters) parameters = 'auto';//auto cookie domain
		this.ga('create', trackerID, parameters);
		// anonymize ip after creating the tracker else the setting is ignored
		this.setAnonymizeIp(!dontAnonymizeIp);
		this.ga(SET, 'hitCallback', this._hitCallback.bind(this));//for dequeueing
		this._appendCallback(callback);
	};

	destroy() {
		clearTimeout(this._replenishInterval);
	}

	/**
	 * En/disable service
	 * @param {Boolean} value Default: true.
	 */
	setEnabled(value) {
		this._enabled = value;
	};
	getEnabled() {
		return this._enabled;
	};

	/**
	 * En/disable queueing of hits when track rate limit is exceeded.
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/limits-quotas
	 * Setting to false will not influence existing queue execution!
	 * @param {Boolean} value Default: true.
	 */
	setQueueEnabled(value) {
		if (value) this._track = this._queue;
		else this._track = this._execute;
	};
	getQueueEnabled() {
		return this._track == this._queue;
	};

	/**
	 * En/disable ip anonymization. This is turned on by default in initialize()
	 * @param {Boolean} value
	 */
	setAnonymizeIp(value) {
		this.ga(SET, 'anonymizeIp', value);
	}

	/**
	 * Set the user ID of signed-in user
	 * @param {String} value User ID of signed-in user
	 * @returns {undefined}
	 */
	setUserID(value) {
		if (!this._enabled) return;
		this._track(SET, [USERID, value]);
	}

	/**
	 * Set value for custom dimension. This value will be used for all subsequent hits.
	 * Reset by setting to null.
	 * Dimensions should be configured in GA account:
	 * https://support.google.com/analytics/answer/2709829
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/custom-dims-mets
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#dimension
	 * @param {Int} index Index of custom dimension as defined in Google Analytics property settings. 
	 * @param {String} value Value for custom dimension
	 */
	setDimension(index, value) {
		if (!this._enabled) return;
		this._track(SET, [DIMENSION + index, value]);
	}

	/**
	 * Set value for custom metric. This value will be used for all subsequent hits.
	 * Reset by setting to null.
	 * Metrics should be configured in GA account:
	 * https://support.google.com/analytics/answer/2709829
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#metric
	 * @param {Int} index Index of custom metric as defined in Google Analytics property settings.
	 * @param {Number} value Numeric value for custom metric
	 */
	setMetric(index, value) {
		if (!this._enabled) return;
		this._track(SET, [METRIC + index, value]);
	}

	/**
	 * Send a hit of type 'event' 
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/events
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#events
	 * @param {String} category
	 * @param {String} action
	 * @param {String} label
	 * @param {Number} value
	 * @param {Object} fields Optional fieldsObject for sending things like {'sessionControl':'end'}, custom dimensions etc
	 * @param {Function} callback
	 */

	trackEvent(category, action, label, value, fields, callback) {
		if (!this._enabled) return;
		var params = [EVENT, category, action, label, value];
		if (fields) params.push(fields);
		console.log(TAG + ".trackEvent " + params.join(","));
		this._track(SEND, params, callback);
	}

	/**
	 * Send a hit of type 'timing' 
	 * NB: user timings are subject to sampling at lower thresholds than normal hits
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/user-timings
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#timing
	 * @param {String} timingCategory
	 * @param {String} timingVar
	 * @param {Number} timingValue In milliseconds
	 * @param {String} timingLabel Optional
	 * @param {Object} fields Optional fieldsObject for sending things like {'sessionControl':'end'}, custom dimensions etc
	 * @param {Function} callback
	 */
	trackTiming(timingCategory, timingVar, timingValue, timingLabel, fields, callback) {
		if (!this._enabled) return;
		var params = [TIMING, timingCategory, timingVar, timingValue, timingLabel];
		if (fields) params.push(fields);
		console.log(TAG + ".trackTiming " + params.join(","));
		this._track(SEND, params, callback);
	}

	/**
	 * Send a hit of type 'pageview' 
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/pages
	 * @param {String} page
	 * @param {Object} fields Optional fieldsObject for sending things like {'sessionControl':'end'}, custom dimensions etc
	 * @param {Function} callback
	 */
	trackPageview(page, fields, callback) {
		if (!this._enabled) return;
		console.log(TAG + ".trackPageview " + page);
		//page could be used to send a fieldsObject!
		if ((typeof page === 'string' || page instanceof String) && page.charAt(0) != "/") page = "/" + page;
		var params = [PAGEVIEW, page];
		if (fields) params.push(fields);
		this._track(SEND, params, callback);
	}

	/**
	 * Send a hit of type 'exception' 
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/exceptions
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/field-reference#exception
	 * @param {String} description Error message, max 150 chars.
	 * @param {Boolean} isFatal Boolean indicating if this was a fatal exception.
	 * @param {Function} callback
	 */
	trackException(description, isFatal, callback) {
		if (!this._enabled) return;
		console.log(TAG + ".trackException " + description + ", fatal: " + isFatal);
		this._track(SEND, [EXCEPTION, { exDescription: description, exFatal: isFatal }], callback);
	}
	
	/**
	 * Sets page for all subsequent hits and sends a pageview hit (optionally)
	 * This is a best practice for SPAs (Single Page Applications)
	 * https://developers.google.com/analytics/devguides/collection/analyticsjs/single-page-applications#overview
	 * @param {String} page
	 * @param {Boolean} dontSend Don't send as pageview (only set). Default: false
	 * @param {Function} callback
	 */
	setPage(page, dontSend, callback) {
		if (!this._enabled) return;
		console.log(TAG + ".setPage " + page + ", dontSend:" + dontSend);
		if (page.charAt(0) != "/") page = "/" + page;
		this._track(SET, [PAGE, page], dontSend ? callback : null);
		if (!dontSend) this._track(SEND, [PAGEVIEW], callback);
	}

	// PRIVATE METHODS

	/**
	 * Central method for all ga commands
	 * Params is array containing parameters but not the callback!
	 * @param {String} method
	 * @param {Array} params
	 * @param {Function} callback
	 */
	_execute(method, params, callback) {
		if (method == SEND) {
			//adjust hit counters
			this._hitsRemaining = Math.max(0, this._hitsRemaining - 1);
			//check session limit
			this._hitCount++;
			if (this._hitCount == HITS_PER_SESSION_LIMIT) {
				console.warn(TAG + "._execute, session limit reached (" + HITS_PER_SESSION_LIMIT + "). Further hits will be ignored.");
			}
		}
		//execute the method on ga
		if (!params) params = [];
		params.unshift(method);
		ga.apply(null, params);
		this._appendCallback(callback);
	}

	/**
	 * 
	 * @param {String} method ga method to execute ('send', 'set' etc)
	 * @param {Array} params. For 'send' event : [hitType, category, action, label, value]
	 * @param {Function} callback
	 */
	_queue(method, params, callback) {
		this._callQueue.push([method, params, callback]);
		this._dequeue();
	}
	_dequeue() {
		if (this._callQueue.length) {
			if (this._hitsRemaining) {
				this._execute.apply(this, this._callQueue.shift());
			}
		}
	}
	_replenish() {
		this._hitsRemaining = Math.min(HITS_INITIAL, this._hitsRemaining + HITS_REPLENISH_INCREMENT);
		//console.log(TAG+"._replenish "+this._hitsRemaining);
		this._dequeue();
	}

	_appendCallback(callback) {
		if (!this._enabled) return;
		//Just add callback to the queue because it will be executed when ga finishes the 'send'
		if (callback != null) this.ga(callback);
	}

	_hitCallback() {
		this._dequeue();
	}

}

//String literals used by GA
const SEND = "send", SET = "set", DIMENSION = "dimension", METRIC = "metric", USERID = "userId";
const EVENT = "event", TIMING = "timing", PAGEVIEW = "pageview", PAGE = "page", EXCEPTION = "exception";
//Constants for managing limits
/*
https://developers.google.com/analytics/devguides/collection/analyticsjs/limits-quotas
10 million hits per month per property
500 hits per session
'The first 10 event hits sent to Analytics are tracked immediately, thereafter tracking is rate limited to one event hit per second.' 
For ga.js 10 / 1. For analytics.js (which we use) 20 / 2
Hits are ignored above this limit. Callback are executed as if the events were properly sent and received though!
There is no way to handle 'errors' of this kind.
That's why I've implemented our own bookkeeping of remaining event hits. 
Drawback of this is that the queueing and scheduling cause the timestamps of the hits to be inaccurate.
This inaccuracy increases with the length of the queue of course.
Best practice is to keep the amount of hits low. Be selective.
Use setQueueEnabled(false) to disable queueing.
*/
const HITS_REPLENISH_INTERVAL = 1050;//actually 1000, + safety margin
const HITS_REPLENISH_INCREMENT = 2;
const HITS_INITIAL = 18;//actually 20, + safety margin
const HITS_PER_SESSION_LIMIT = 500;


const TAG = "GoogleAnalyticsService";//used for logging/debugging